import { useEffect, useMemo, useState } from 'react';
import { KeyRound, Mail, RefreshCw, Send } from 'lucide-react';
import {
  useEmailSettings,
  useRegenerateInboundKey,
  useSendTestEmail,
  useUpdateEmailSettings,
} from '@/api/email';
import {
  Button,
  Card,
  Checkbox,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Segmented,
  Skeleton,
  Textarea,
  useToast,
} from '@/components/ui';
import { CopyBlock } from '@/components/ui/CopyBlock';
import { cn } from '@/lib/cn';
import { buildEmailPatch, draftFromSettings, type EmailDraft, type EmailSecretsDraft } from './email-draft';

export function EmailPage() {
  const settings = useEmailSettings();
  const update = useUpdateEmailSettings();
  const toast = useToast();
  const [draft, setDraft] = useState<EmailDraft | null>(null);
  const [secrets, setSecrets] = useState<EmailSecretsDraft>({
    clearRelayAuthValue: false,
    clearSmtpPassword: false,
  });
  const [confirmRotate, setConfirmRotate] = useState(false);

  const s = settings.data;
  useEffect(() => {
    if (s) {
      setDraft(draftFromSettings(s));
      setSecrets({ clearRelayAuthValue: false, clearSmtpPassword: false });
    }
  }, [s]);

  const patch = useMemo(() => (s && draft ? buildEmailPatch(draft, secrets, s) : null), [s, draft, secrets]);
  const dirty = !!patch && Object.keys(patch).length > 0;
  const set = (p: Partial<EmailDraft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  if (settings.isPending || !s || !draft) return <Skeleton className="h-[32rem] rounded-2xl" />;
  if (settings.error) {
    return (
      <Card>
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      </Card>
    );
  }

  const toggleEnabled = (isEnabled: boolean) =>
    update.mutate(
      { isEnabled },
      {
        onSuccess: () => toast.success(isEnabled ? 'Email channel enabled' : 'Email channel paused'),
        onError: (e) => toast.error(e.message),
      },
    );

  const save = () =>
    update.mutate(patch!, {
      onSuccess: () => toast.success('Email settings saved'),
      onError: (e) => toast.error(e.message),
    });

  return (
    <>
      <PageHeader
        title="Email channel"
        description="Put your support address in front of customers: every email becomes a ticket that the workflow engine can route to an agent, team or teammate — replies go back out as email."
      />

      <Card className="overflow-hidden">
        <div className="from-brand-50/80 to-accent-50/50 flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r px-5 py-4">
          <span className="brand-gradient flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm">
            <Mail className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <p className="font-semibold text-slate-900">Support email</p>
            <p className="text-xs text-slate-500">
              Inbound webhook receives mail; agent and AI replies are sent back on the same thread.
            </p>
          </div>
          <Checkbox
            label="Enabled"
            checked={s.isEnabled}
            disabled={update.isPending}
            onChange={(e) => toggleEnabled(e.target.checked)}
          />
        </div>

        <div className={cn('grid gap-6 p-5 lg:grid-cols-2', !s.isEnabled && 'opacity-60')}>
          <section className="space-y-4">
            <h3 className="text-sm font-bold tracking-wide text-slate-500 uppercase">Inbound</h3>
            <Input
              label="Support address"
              placeholder="support@yourcompany.com"
              value={draft.inboundAddress}
              onChange={(e) => set({ inboundAddress: e.target.value })}
              hint="Customers write to this address. Replies use support+t<number>@… so they thread back onto the right ticket."
            />
            <CopyBlock
              label="Inbound webhook URL"
              value={s.inboundWebhookUrl}
              icon={<KeyRound className="h-4 w-4" />}
              hint="Point a Mailgun route, Postmark inbound stream, SendGrid Inbound Parse — or any forwarder that POSTs JSON — at this URL. The key is the secret; rotate it if it leaks."
            />
            <div className="flex justify-end">
              <Button variant="secondary" size="sm" onClick={() => setConfirmRotate(true)}>
                <RefreshCw className="h-3.5 w-3.5" /> Rotate key
              </Button>
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-sm font-bold tracking-wide text-slate-500 uppercase">Outbound</h3>
            <Segmented
              label="Outbound transport"
              value={draft.transport}
              onChange={(transport) => set({ transport })}
              options={[
                { value: 'relay', label: 'HTTPS relay' },
                { value: 'smtp', label: 'SMTP' },
              ]}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="From name"
                placeholder="Acme Support"
                value={draft.fromName}
                onChange={(e) => set({ fromName: e.target.value })}
              />
              <Input
                label="From address"
                placeholder="support@yourcompany.com"
                value={draft.fromAddress}
                onChange={(e) => set({ fromAddress: e.target.value })}
              />
            </div>
            <Input
              label="Reply-to (optional)"
              placeholder="replies@yourcompany.com"
              value={draft.replyTo}
              onChange={(e) => set({ replyTo: e.target.value })}
              hint="Leave empty to use the per-ticket plus address, which guarantees reliable threading."
            />

            {draft.transport === 'relay' ? (
              <div className="space-y-4">
                <Input
                  label="Relay URL"
                  type="url"
                  placeholder="https://relay.yourcompany.com/email"
                  value={draft.relayUrl}
                  onChange={(e) => set({ relayUrl: e.target.value })}
                  hint="Receives a JSON body with from, to, subject, text and messageId; any 2xx means sent."
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Auth header (optional)"
                    placeholder="Authorization"
                    value={draft.relayAuthHeader}
                    onChange={(e) => set({ relayAuthHeader: e.target.value })}
                  />
                  <SecretInput
                    label="Auth value"
                    hasStored={s.hasRelayAuthValue}
                    last4={s.relayAuthValueLast4}
                    value={draft.relayAuthValue}
                    onChange={(relayAuthValue) => set({ relayAuthValue })}
                    cleared={secrets.clearRelayAuthValue}
                    onClear={(clearRelayAuthValue) => setSecrets((c) => ({ ...c, clearRelayAuthValue }))}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-[1fr_7rem]">
                  <Input
                    label="SMTP host"
                    placeholder="smtp.yourcompany.com"
                    value={draft.smtpHost}
                    onChange={(e) => set({ smtpHost: e.target.value })}
                  />
                  <Input
                    label="Port"
                    inputMode="numeric"
                    value={draft.smtpPort}
                    onChange={(e) => set({ smtpPort: e.target.value })}
                  />
                </div>
                <Checkbox
                  label="Implicit TLS"
                  description="On for port 465; leave off for STARTTLS (587)."
                  checked={draft.smtpSecure}
                  onChange={(e) => set({ smtpSecure: e.target.checked })}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Username"
                    placeholder="mailer@yourcompany.com"
                    value={draft.smtpUsername}
                    onChange={(e) => set({ smtpUsername: e.target.value })}
                  />
                  <SecretInput
                    label="Password"
                    hasStored={s.hasSmtpPassword}
                    last4={s.smtpPasswordLast4}
                    value={draft.smtpPassword}
                    onChange={(smtpPassword) => set({ smtpPassword })}
                    cleared={secrets.clearSmtpPassword}
                    onClear={(clearSmtpPassword) => setSecrets((c) => ({ ...c, clearSmtpPassword }))}
                  />
                </div>
              </div>
            )}

            <Textarea
              label="Signature"
              placeholder="— The Acme team"
              rows={3}
              value={draft.signature}
              onChange={(e) => set({ signature: e.target.value })}
              hint="Appended to every outbound email."
            />
          </section>
        </div>

        <div className="border-t border-slate-100 px-5 py-4">
          <h3 className="mb-3 text-sm font-bold tracking-wide text-slate-500 uppercase">
            Instant acknowledgment
          </h3>
          <div className="grid items-start gap-4 lg:grid-cols-[18rem_1fr]">
            <Checkbox
              label="Send an acknowledgment"
              description="Confirms the ticket number the moment an email arrives. Customer replies thread automatically."
              checked={draft.autoAckEnabled}
              disabled={!s.isEnabled}
              onChange={(e) => set({ autoAckEnabled: e.target.checked })}
            />
            <Textarea
              label="Acknowledgment message"
              rows={3}
              disabled={!s.isEnabled || !draft.autoAckEnabled}
              value={draft.autoAckBody}
              onChange={(e) => set({ autoAckBody: e.target.value })}
              hint="{number} becomes the ticket number, {name} the sender's name. Your signature is appended and auto-replies are never acknowledged."
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-5 py-3">
          <p className="text-xs text-slate-500">
            {s.isEnabled
              ? 'Inbound email creates tickets; replies are emailed automatically.'
              : 'The channel is paused — the webhook refuses mail until you enable it.'}
          </p>
          <Button disabled={!dirty} loading={update.isPending} onClick={save}>
            Save changes
          </Button>
        </div>
      </Card>

      <TestEmailCard defaultTo={s.inboundAddress} />

      <Modal
        open={confirmRotate}
        onClose={() => setConfirmRotate(false)}
        title="Rotate the inbound key?"
        description="The current webhook URL stops working immediately. Update your email provider's route to the new URL afterwards."
        size="sm"
        footer={<RotateFooter onClose={() => setConfirmRotate(false)} />}
      >
        <p className="text-sm text-slate-600">
          Emails sent to the old URL will be rejected until the provider is updated.
        </p>
      </Modal>
    </>
  );
}

function RotateFooter({ onClose }: { onClose: () => void }) {
  const rotate = useRegenerateInboundKey();
  const toast = useToast();
  return (
    <>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="danger"
        loading={rotate.isPending}
        onClick={() =>
          rotate.mutate(undefined, {
            onSuccess: () => {
              toast.success('Inbound key rotated — copy the new webhook URL');
              onClose();
            },
            onError: (e) => toast.error(e.message),
          })
        }
      >
        Rotate key
      </Button>
    </>
  );
}

function SecretInput({
  label,
  hasStored,
  last4,
  value,
  onChange,
  cleared,
  onClear,
}: {
  label: string;
  hasStored: boolean;
  last4: string;
  value: string;
  onChange: (v: string) => void;
  cleared: boolean;
  onClear: (v: boolean) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Input
        label={label}
        type="password"
        autoComplete="new-password"
        placeholder={hasStored && !cleared ? `Saved ••••${last4}` : 'Not set'}
        value={value}
        disabled={cleared}
        onChange={(e) => onChange(e.target.value)}
      />
      {hasStored && !value && (
        <Checkbox
          label={cleared ? 'Will be removed on save' : 'Remove saved value'}
          checked={cleared}
          onChange={(e) => onClear(e.target.checked)}
        />
      )}
    </div>
  );
}

function TestEmailCard({ defaultTo }: { defaultTo: string }) {
  const send = useSendTestEmail();
  const toast = useToast();
  const [to, setTo] = useState('');
  useEffect(() => {
    if (defaultTo && !to) setTo(defaultTo);
  }, [defaultTo, to]);

  return (
    <Card className="mt-6 p-5">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          label="Send a test email"
          placeholder="you@yourcompany.com"
          wrapperClassName="min-w-64 flex-1"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <Button
          loading={send.isPending}
          disabled={!to}
          onClick={() =>
            send.mutate(
              { to },
              {
                onSuccess: (r) => (r.ok ? toast.success(r.message) : toast.error(r.message)),
                onError: (e) => toast.error(e.message),
              },
            )
          }
        >
          <Send className="h-4 w-4" /> Send test
        </Button>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        Uses the transport configured above, so save your changes first.
      </p>
    </Card>
  );
}
